<template>
  <div>
    <b-row>
      <b-col class="mb-3" cols="12">
        <h1>Отчет по таможенному оформлению</h1>
      </b-col>
    </b-row>

    <!-- Фильтры -->
    <b-card class="mb-3">
      <b-card-body class="py-2">
        <b-row class="align-items-end">
          <!-- Период -->
          <b-col md="3">
            <label class="mb-1">Период</label>
            <b-form-select
              v-model="filters.month"
              :options="monthOptions"
              @change="onFiltersChange"
              size="sm"
            />
          </b-col>

          <!-- Пользователь -->
          <b-col md="3" v-if="!isToManager">
            <label class="mb-1">Сотрудник</label>
            <b-form-select
              v-model="filters.user_id"
              :options="userOptions"
              @change="onFiltersChange"
              size="sm"
            >
              <template #first>
                <b-form-select-option :value="null">Все сотрудники</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>

          <!-- Статус -->
          <b-col md="3">
            <label class="mb-1">Статус</label>
            <b-form-select
              v-model="filters.status_id"
              :options="statusOptions"
              @change="onFiltersChange"
              size="sm"
            />
          </b-col>

          <!-- Индикатор загрузки -->
          <b-col md="3" class="d-flex align-items-center mb-2">
            <b-spinner v-if="loading" small class="mr-2" />
            <small v-if="loading" class="text-muted">Загрузка...</small>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Результаты -->
    <b-card v-if="reportData.length > 0">
      <b-card-header class="py-2">
        <h6 class="mb-0">Результаты отчета</h6>
      </b-card-header>
      <b-card-body class="py-2">
        <!-- Таблица результатов -->
        <b-table
          :items="reportData"
          :fields="tableFields"
          striped
          hover
          responsive
          class="mb-4"
          small
        >
          <template #cell(order_id)="row">
            <a 
              href="#" 
              @click.prevent="openOrderSidebar(row.item.order_id)"
              class="text-primary"
            >
              {{ row.item.order_id }}
            </a>
          </template>
          <template #cell(dt_numbers)="row">
            <span v-if="row.item.dt_numbers.length > 0">
              {{ row.item.dt_numbers.join(', ') }}
            </span>
            <span v-else class="text-muted">-</span>
          </template>
        </b-table>

        <!-- Итоги -->
        <b-alert variant="info" show class="py-2">
          <div class="d-flex justify-content-between align-items-center">
            <span class="font-weight-bold">Итоги:</span>
            <div>
              <span class="mr-3">Поставок: <strong>{{ summary.total_orders }}</strong></span>
              <span>ДТ: <strong>{{ summary.total_dt_count }}</strong></span>
            </div>
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- Сообщение об отсутствии данных -->
    <b-alert v-else-if="!loading && reportData.length === 0" variant="warning" show>
      По выбранным критериям данные не найдены.
    </b-alert>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import { openOrderSidebar } from "@/mixins/openOrderSidebar";
import { 
  API_TO_ACTIVITY_REPORT_DATA, 
  API_TO_ACTIVITY_REPORT_STATUSES, 
  API_TO_ACTIVITY_REPORT_USERS 
} from "@/constants";

export default {
  name: "OrderTOActivityReport",
  
  mixins: [openOrderSidebar],

  data() {
    return {
      loading: false,
      loadTimeout: null,
      reportData: [],
      summary: {
        total_orders: 0,
        total_dt_count: 0
      },
      filters: {
        month: null,
        user_id: null,
        status_id: null
      },
      userOptions: [],
      statusOptions: [],
      monthOptions: [],
      tableFields: [
        { key: 'user_name', label: 'Менеджер', sortable: true, thClass: 'width180px' },
        { key: 'order_id', label: 'ID поставки', sortable: true },
        { key: 'company_name', label: 'Наименование клиента', sortable: true },
        { key: 'dt_numbers', label: 'Выпущены ДТ', sortable: false },
        { key: 'dt_count', label: 'Кол-во', sortable: true }
      ]
    };
  },

  computed: {
    ...mapGetters("auth", ["currentUser", "statusUser", "headDepartment", "roleNoHead"]),
    
    maxDate() {
      return new Date().toISOString().split('T')[0];
    },

    isToManager() {
      return this.currentUser.role.includes("TO-manager");
    }
  },

  async created() {
    // Генерируем опции месяцев
    this.generateMonthOptions();
    
    // Устанавливаем текущий месяц по умолчанию
    const now = new Date();
    this.filters.month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    // Загружаем справочники
    await this.loadStatuses();
    await this.loadUsers();
    
    // Если пользователь TO, устанавливаем его по умолчанию
    if (this.currentUser.role.includes("TO-manager") ||
        this.currentUser.role.includes("TO-head-manager")) {
      this.filters.user_id = this.currentUser.id;
    }
    
    // Автоматически загружаем данные после установки фильтров
    this.loadReportData();
  },

  methods: {
    generateMonthOptions() {
      const options = [];
      const now = new Date();
      
      // Генерируем последние 24 месяца
      for (let i = 0; i < 24; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const monthName = date.toLocaleDateString('ru', { month: 'long' });
        
        // Делаем первую букву заглавной
        const capitalizedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        options.push({
          value: `${year}-${month}`,
          text: `${capitalizedMonthName} ${year}`
        });
      }
      
      this.monthOptions = options;
    },

    async loadStatuses() {
      try {
        const response = await axios.get(API_TO_ACTIVITY_REPORT_STATUSES);
        if (response.data.success) {
          this.statusOptions = response.data.data.map(status => ({
            value: status.id,
        text: status.name
          }));
          
          // Устанавливаем "Закрыт" по умолчанию
          const anyStatus = this.statusOptions.find(s => s.text.includes('Закрыт'));
          if (anyStatus) {
            this.filters.status_id = anyStatus.value;
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки статусов:', error);
        this.$bvToast.toast('Ошибка загрузки статусов', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    async loadUsers() {
      try {
        const response = await axios.get(API_TO_ACTIVITY_REPORT_USERS);
        if (response.data.success) {
          this.userOptions = response.data.data.map(user => ({
            value: user.id,
            text: user.name
          }));
        }
      } catch (error) {
        console.error('Ошибка загрузки пользователей:', error);
        this.$bvToast.toast('Ошибка загрузки пользователей', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },

    async loadReportData() {
      if (!this.filters.month) {
        this.$bvToast.toast('Выберите период', {
          title: 'Предупреждение',
          variant: 'warning',
          solid: true
        });
        return;
      }

      this.loading = true;
      
      try {
        // Формируем даты напрямую из строки месяца
        const [year, month] = this.filters.month.split('-');
        
        // Первый день месяца
        const startDate = `${year}-${month}-01`;
        
        // Последний день месяца
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
        
        const params = {
          start_date: startDate,
          end_date: endDate
        };

        if (this.filters.user_id) {
          params.user_ids = [this.filters.user_id];
        }

        if (this.filters.status_id) {
          params.status_ids = [this.filters.status_id];
        }

        const response = await axios.get(API_TO_ACTIVITY_REPORT_DATA, { params });
        
        if (response.data.success) {
          this.reportData = response.data.data;
          this.summary = response.data.summary;
        } else {
          this.$bvToast.toast('Ошибка загрузки данных отчета', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error);
        this.$bvToast.toast('Ошибка загрузки данных отчета', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },

    onFiltersChange() {
      // Автоматически перезагружаем данные при изменении фильтров с небольшой задержкой
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
      }
      this.loadTimeout = setTimeout(() => {
        this.loadReportData();
      }, 300);
    }
  },

  beforeDestroy() {
    // Очищаем таймаут при уничтожении компонента
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
    }
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.card-body {
  padding: 0.75rem;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-size: 0.875rem;
}

.table td {
  font-size: 0.875rem;
}

.alert ul {
  margin-bottom: 0;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
}

h1 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

h5 {
  font-size: 1rem;
}
</style>
